/**
 * @format
 */
import reducers from './reducers';
import * as types from './actions';
import request from '../../helpers/request';
import { enqueueNotification } from '../app';

export default reducers;

const findTaskById = (tasks, taskId) => {
  if (tasks[taskId]) {
    const keys = Object.keys(tasks);
    const index = keys.indexOf(taskId);
    return { ...tasks[taskId], __index: index };
  }

  for (const parentId in tasks) {
    const parentTask = tasks[parentId];
    const subTasks = parentTask.sub_tasks || [];
    const index = subTasks.findIndex(sub => sub.id === taskId);

    if (index !== -1) {
      return {
        ...subTasks[index],
        parent_id: parentId,
        __index: index,
      };
    }
  }

  return null;
};

export const addTask = pitchData => {
  return async dispatch => {
    dispatch({ type: types.CREATE_TASK_LOADING });

    try {
      const { data } = await request.post(`/api/tasks/v1`, pitchData, dispatch);

      if (data.success) {
        dispatch({
          type: types.CREATE_TASK_SUCCESS,
          payload: {
            taskId: data.data.id,
            task: data.data,
          },
        });
        return Promise.resolve(data);
      } else {
        const message = data.message;
        dispatch({
          type: types.CREATE_TASK_FAILURE,
          payload: message,
        });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err) {
      const message =
        'Something went wrong while creating task, please try again.';
      dispatch({
        type: types.CREATE_TASK_FAILURE,
        payload: message,
      });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};

export const loadAllTask = (params = {}) => {
  return async dispatch => {
    dispatch({ type: types.GET_ALL_TASKS_LOADING });

    try {
      const { data } = await request.get(`/api/tasks/v1`, params, dispatch);
      if (data.success) {
        dispatch({
          type: types.GET_ALL_TASKS_SUCCESS,
          payload: {
            tasks: data.data,
          },
        });
        return Promise.resolve(data);
      } else {
        const message = data.message;
        dispatch({
          type: types.GET_ALL_TASKS_FAILURE,
          payload: message,
        });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err) {
      const message =
        'Something went wrong while loading tasks, please try again.';
      dispatch({
        type: types.GET_ALL_TASKS_FAILURE,
        payload: message,
      });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};

export const loadTaskById = (taskId, params = {}) => {
  return async dispatch => {
    dispatch({ type: types.GET_TASK_LOADING });

    try {
      const { data } = await request.get(
        `/api/tasks/v1/${taskId}`,
        params,
        dispatch
      );

      if (data.success) {
        dispatch({
          type: types.GET_TASK_SUCCESS,
          payload: {
            task: data.data,
          },
        });
        return Promise.resolve(data);
      } else {
        const message = data.message;
        dispatch({
          type: types.GET_ALL_TASKS_FAILURE,
          payload: message,
        });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err) {
      const message =
        'Something went wrong while loading task, please try again.';
      dispatch({
        type: types.GET_TASK_FAILURE,
        payload: message,
      });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};

export const updateTasks = pitchData => {
  return async (dispatch, getState) => {
    const { tasks_to_update, attributesToUpdate, newTasksOrder } = pitchData;
    let attributesToUpdateCopy;

    const stateTasks = getState()?.tasks?.tasks;

    // Backup current versions for rollback
    const originalTasks = JSON.parse(JSON.stringify(stateTasks));

    // Apply optimistic updates locally
    const optimisticallyUpdatedTasks = tasks_to_update
      .map(id => {
        if (attributesToUpdate.assignees !== undefined) {
          attributesToUpdateCopy = {
            ...pitchData.attributesToUpdate, // preserve other attributes if needed
            assignees: (attributesToUpdate['assignees'] || []).map(user => ({
              user: user,
            })),
          };
        }
        // Check if it's a top-level task
        if (stateTasks[id]) {
          return {
            ...stateTasks[id],
            ...(Object.keys(attributesToUpdate)[0] === 'assignees'
              ? attributesToUpdateCopy
              : attributesToUpdate),
          };
        }

        // It's a subtask — find the parent and subtask
        const parentEntry = Object.values(stateTasks).find(task =>
          task.sub_tasks?.some(st => st.id === id)
        );

        if (parentEntry) {
          const subtask = parentEntry.sub_tasks.find(st => st.id === id);
          return {
            ...subtask,
            ...(Object.keys(attributesToUpdate)[0] === 'assignees'
              ? attributesToUpdateCopy
              : attributesToUpdate),
            parent_id: parentEntry.id,
          };
        }

        return null;
      })
      .filter(Boolean); // Remove any nulls

    dispatch({
      type: types.OPTIMISTIC_TASK_UPDATE,
      payload: {
        updatedTasks: optimisticallyUpdatedTasks,
        newTasksOrder: newTasksOrder,
      },
    });

    if (attributesToUpdate.assignees !== undefined) {
      pitchData = {
        ...pitchData,
        attributesToUpdate: {
          ...pitchData.attributesToUpdate, // preserve other attributes if needed
          assignees: (attributesToUpdate['assignees'] || []).map(
            user => user.id
          ),
        },
      };
    }

    try {
      const { data } = await request.put('/api/tasks/v1', pitchData, dispatch);
      if (data.success) {
        dispatch({
          type: types.UPDATE_TASK_SUCCESS,
          payload: { tasks: data.data },
        });
        return Promise.resolve(data);
      } else {
        const message = data.message;
        dispatch({ type: types.ROLLBACK_TASK_UPDATE, payload: originalTasks });
        dispatch({ type: types.UPDATE_TASK_FAILURE, payload: { message } });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err) {
      const message =
        'Something went wrong while updating tasks, please try again.';
      dispatch({ type: types.ROLLBACK_TASK_UPDATE, payload: originalTasks });
      dispatch({ type: types.UPDATE_TASK_FAILURE, payload: { message } });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};

export const getFilteredTask = filters => {
  const filtersAndSort = filters.filters;
  const isFiltersEmptyOrSortEmpty =
    Object.keys(filtersAndSort).length === 0 ||
    (filtersAndSort.sort && Object.keys(filtersAndSort.sort).length === 0);

  return async dispatch => {
    dispatch({ type: types.GET_ALL_TASKS_LOADING });

    try {
      const { data } = await request.get(
        `/api/tasks/v2/resource`,
        filters,
        dispatch
      );
      if (data.success) {
        dispatch({
          type: types.GET_ALL_TASKS_SUCCESS,
          payload: {
            tasks: data.data,
            isEmptyFilters: isFiltersEmptyOrSortEmpty,
          },
        });
        return Promise.resolve(data);
      } else {
        const message = data.message;
        dispatch({
          type: types.GET_ALL_TASKS_FAILURE,
          payload: message,
        });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err) {
      const message =
        'Something went wrong while loading tasks, please try again.';
      dispatch({
        type: types.GET_ALL_TASKS_FAILURE,
        payload: message,
      });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};

export const deleteTask = (taskId, option) => {
  return async (dispatch, getState) => {
    const state = getState();
    const tasks = state.tasks.tasks;

    // Backup for task OR subtask
    const taskToDelete = findTaskById(tasks, taskId);

    dispatch({
      type: types.OPTIMISTIC_DELETE_TASK,
      payload: {
        taskId,
        backup: taskToDelete,
      },
    });

    try {
      const { data } = await request.del(
        `/api/tasks/v1/${taskId}`,
        option,
        dispatch
      );

      if (data.success) {
        dispatch({
          type: types.DELETE_TASK_SUCCESS,
          payload: {
            taskId,
          },
        });
        dispatch(enqueueNotification('Task Deleted!'));
        return Promise.resolve(data);
      } else {
        const message = data.message;
        dispatch({
          type: types.DELETE_TASK_FAILURE,
          payload: message,
        });
        dispatch(enqueueNotification(message));
        return Promise.reject(new Error(message));
      }
    } catch (err) {
      dispatch({
        type: types.REVERT_DELETE_TASK,
        payload: {
          taskId,
          backup: taskToDelete,
        },
      });

      const message =
        'Something went wrong while deleting tasks, please try again.';
      dispatch({
        type: types.DELETE_TASK_FAILURE,
        payload: message,
      });
      dispatch(enqueueNotification(message));
      return Promise.reject(err);
    }
  };
};
